/**
 * T114-F5-f / T115-F3: Batch Actions Toolbar
 *
 * Mobile-only fixed bottom bar with deferred unmount for exit animation.
 * Desktop batch actions are now embedded in UnifiedTopBar (Zone D).
 *
 * Rendered via React Portal to document.body to escape SidebarInset's
 * `position: relative` + `overflow: hidden` which clips fixed-position
 * descendants on mobile browsers (WebKit/Blink bug).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useFABContext } from '@/components/common/FABContext';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { IconButton } from '@/components/ui/icon-button';
import { Palette, Hash, Trash2, Wand2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BatchActionsToolbarProps {
  selectedCount: number;
  onEditCategory: () => void;
  onAddRemoveTags: () => void;
  onDelete: () => void;
  onApplyRule: () => void;
  onClearSelection: () => void;
}

export function BatchActionsToolbar({
  selectedCount,
  onEditCategory,
  onAddRemoveTags,
  onDelete,
  onApplyRule,
  onClearSelection,
}: BatchActionsToolbarProps) {
  // Hide FAB when batch selection is active
  const { setHideFAB } = useFABContext();
  useEffect(() => {
    setHideFAB(selectedCount > 0);
    return () => setHideFAB(false);
  }, [selectedCount, setHideFAB]);

  // Deferred unmount pattern for exit animation
  const [isExiting, setIsExiting] = useState(false);
  const prevSelectedRef = useRef(selectedCount);

  useEffect(() => {
    if (prevSelectedRef.current > 0 && selectedCount === 0) {
      setIsExiting(true);
    }
    // Reset isExiting when selection comes back during exit transition
    if (selectedCount > 0 && isExiting) {
      setIsExiting(false);
    }
    prevSelectedRef.current = selectedCount;
  }, [selectedCount, isExiting]);

  // Safety timeout fallback in case onTransitionEnd never fires
  useEffect(() => {
    if (isExiting) {
      const timer = setTimeout(() => setIsExiting(false), 250);
      return () => clearTimeout(timer);
    }
  }, [isExiting]);

  const handleClearWithExit = useCallback(() => {
    setIsExiting(true);
    // Actual clear happens after transition completes
    setTimeout(() => onClearSelection(), 200);
  }, [onClearSelection]);

  const handleTransitionEnd = useCallback(() => {
    if (isExiting) {
      setIsExiting(false);
    }
  }, [isExiting]);

  // Only render mobile bar when visible or exiting
  if (selectedCount === 0 && !isExiting) return null;

  return createPortal(
    <div
      className={cn(
        'md:hidden fixed bottom-0 left-0 right-0 z-50',
        'bg-background/95 backdrop-blur-sm',
        'border-t border-border shadow-lg',
        'pb-[env(safe-area-inset-bottom)]',
        'transition-transform duration-200 ease-out',
        selectedCount > 0 && !isExiting
          ? 'translate-y-0'
          : 'translate-y-full pointer-events-none',
      )}
      role="toolbar"
      aria-label="Batch actions"
      data-testid="batch-toolbar-mobile"
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="p-3 space-y-2">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <Badge variant="default" className="font-medium">
            {selectedCount} selected
          </Badge>
          <IconButton
            tooltip="Clear selection"
            variant="ghost"
            size="sm"
            className="h-8 w-8 min-h-[44px] min-w-[44px]"
            onClick={handleClearWithExit}
          >
            <X className="h-4 w-4" />
          </IconButton>
        </div>
        {/* Action grid */}
        <div className="grid grid-cols-4 gap-2">
          <Button
            variant="outline"
            className="flex-col h-auto py-2 gap-1 min-h-[44px]"
            onClick={onEditCategory}
          >
            <Palette className="h-4 w-4" />
            <span className="text-[10px]">Category</span>
          </Button>
          <Button
            variant="outline"
            className="flex-col h-auto py-2 gap-1 min-h-[44px]"
            onClick={onAddRemoveTags}
          >
            <Hash className="h-4 w-4" />
            <span className="text-[10px]">Tags</span>
          </Button>
          <Button
            variant="outline"
            className="flex-col h-auto py-2 gap-1 min-h-[44px]"
            onClick={onApplyRule}
          >
            <Wand2 className="h-4 w-4" />
            <span className="text-[10px]">Rule</span>
          </Button>
          <Button
            variant="destructive"
            className="flex-col h-auto py-2 gap-1 min-h-[44px]"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
            <span className="text-[10px]">Delete</span>
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
